import { useTranslation } from 'react-i18next'
import { DataItem } from 'scrivito'

import { Button } from '@/Atomic/Molecules/Button'
import { unLinkDataItem } from '@/Atomic/Organisms/RelationalSublist'
import { SublistItem, SublistItemContent } from '@/Atomic/Templates/Sublist'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes } from '@/Data/typesV2'
import { useUndoableAction } from '@/hooks/useUndoableAction'
import { getEnumDisplayValue } from '@/utils/CRUD/read/enum/getDisplayValue'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'
import { formatDateTime } from '@/utils/formatDateTime'

interface OpportunitySublistItemProps {
  opportunity: DataItem
  relation?: DataItem
  flash?: boolean
  onOpen: (opportunity: DataItem) => void
}

const formatAmount = (rawValue: number | undefined) => {
  if (typeof rawValue !== 'number' || !Number.isFinite(rawValue)) return ''
  return Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rawValue)
}

export const buildOpportunitySubtitle = (
  opportunity: DataItem,
  schemaAttributes?: SchemaAttributes,
) => {
  const totalPriceLabel = schemaAttributes?.totalPrice?.title || 'Revenue'
  const dateLabel = schemaAttributes?.opportunityAt?.title || 'Opportunity date'
  const statusLabel = schemaAttributes?.status?.title || 'Status'
  const typeLabel = schemaAttributes?.type?.title || 'Type'

  const status = opportunity.get('status') as string
  const type = opportunity.get('type') as string
  const dateValue = opportunity.get('opportunityAt') as Date | undefined
  const totalPrice = opportunity.get('totalPrice') as number | undefined
  const currency = (opportunity.get('totalPriceCurrency') as string) || ''

  const statusDisplayValue =
    getEnumDisplayValue(schemaAttributes?.status?.enumValues, status) ?? status
  const typeDisplayValue =
    getEnumDisplayValue(schemaAttributes?.type?.enumValues, type) ?? type

  const amount = formatAmount(totalPrice)
  const amountWithCurrency = [amount, currency].filter(Boolean).join(' ')

  return [
    amountWithCurrency ? `${totalPriceLabel}: ${amountWithCurrency}` : '',
    dateValue ? `${dateLabel}: ${formatDateTime(dateValue, 'PP')}` : '',
    statusDisplayValue ? `${statusLabel}: ${statusDisplayValue}` : '',
    typeDisplayValue ? `${typeLabel}: ${typeDisplayValue}` : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

export const OpportunitySublistItem: React.FC<OpportunitySublistItemProps> = ({
  opportunity,
  relation,
  flash,
  onOpen,
}) => {
  const { t } = useTranslation()
  const { isHidden, run } = useUndoableAction()
  const { schemaAttributes } = getSchemaAttributes(
    getSchemaName(DataClassName.OPPORTUNITY),
  )

  const displayTitle =
    (opportunity.get('displayTitle') as string) ||
    (opportunity.get('keyword') as string) ||
    ''

  if (relation && isHidden(relation.id())) return null

  return (
    <SublistItem flash={flash}>
      <SublistItemContent
        avatarIcon="fire"
        title={displayTitle}
        subtitle={buildOpportunitySubtitle(opportunity, schemaAttributes)}
        onClick={() => onOpen(opportunity)}
        showButton="onHover"
        button={
          relation ? (
            <Button
              variant="icon"
              icon="cross"
              tooltip={t('common.remove')}
              onClick={() => {
                const relationId = relation.id()

                run({
                  id: relationId,
                  onAction: ({ onError, onUndo }) =>
                    unLinkDataItem({
                      relationDataItem: relation,
                      removedListItem: opportunity,
                      onError,
                      onUndo,
                    }),
                })
              }}
            />
          ) : undefined
        }
      />
    </SublistItem>
  )
}
